import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { useIntl } from '../../util/reactIntl';
import { isScrollingDisabled } from '../../ducks/ui.duck';

import { Heading, Page, LayoutSingleColumn, NamedLink, ExternalLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AboutPage.module.css';

// Store links for the mobile apps. The Android button only renders when a
// non-empty URL is provided.
const APP_STORE_URL = 'https://apps.apple.com/us/app/pallet-rack/id6768537627';
const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.prmapp';

// Platform glyphs for the store buttons (inline SVG — no external assets).
const AppleIcon = () => (
  <svg className={css.storeIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path
      fill="currentColor"
      d="M16.365 1.43c0 1.14-.417 2.2-1.11 2.99-.83.95-2.18 1.68-3.29 1.6-.14-1.11.42-2.28 1.09-3.02.75-.83 2.06-1.46 3.31-1.57zM20.5 17.02c-.6 1.38-.89 1.99-1.66 3.21-1.08 1.71-2.6 3.84-4.49 3.85-1.68.02-2.11-1.09-4.39-1.08-2.28.01-2.75 1.1-4.43 1.08-1.89-.02-3.33-1.94-4.41-3.65C-.9 16.9-1.2 11.16 1.36 8.03c1.16-1.44 2.99-2.35 4.7-2.35 1.74 0 2.83 1.09 4.27 1.09 1.4 0 2.25-1.09 4.27-1.09 1.52 0 3.13.83 4.28 2.26-3.76 2.06-3.15 7.43.42 8.98z"
    />
  </svg>
);

const GooglePlayIcon = () => (
  <svg className={css.storeIcon} viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <path fill="#00D2FF" d="M3.6 2.3c-.3.3-.5.8-.5 1.4v16.6c0 .6.2 1.1.5 1.4l.1.1L13 12.1v-.2L3.7 2.2l-.1.1z" />
    <path fill="#FFCE00" d="M16.3 15.3 13 12.1v-.2l3.3-3.2.1.1 3.9 2.2c1.1.6 1.1 1.7 0 2.3l-3.9 2.2-.1-.1z" />
    <path fill="#00F076" d="m16.4 15.2-3.4-3.2L3.6 21.7c.4.4 1 .4 1.7 0l11.1-6.5z" />
    <path fill="#FF3945" d="M16.4 8.8 5.3 2.4c-.7-.4-1.3-.4-1.7 0l9.4 9.6 3.4-3.2z" />
  </svg>
);

/**
 * About Us page — custom, mission-led content for the marketplace.
 *
 * This component replaces the generic Sharetribe page-asset render at /p/about.
 * The route override lives in src/routing/routeConfiguration.js (see the comment
 * there). The page is static: it loads no data and makes no API calls.
 *
 * NOTE: Step 1 renders the page shell (chrome + meta) only. The mission copy and
 * styling are added in Steps 2 and 3.
 *
 * @param {Object} props
 * @param {boolean} props.scrollingDisabled - Whether scrolling is disabled
 * @returns {JSX.Element} About page
 */
export const AboutPageComponent = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const { scrollingDisabled } = props;

  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage({ id: 'AboutPage.schemaTitle' }, { marketplaceName });
  const schemaDescription = intl.formatMessage({ id: 'AboutPage.schemaDescription' });

  // Reuse the marketplace's default social-sharing images (resolved from the
  // hosted branding asset, or the bundled defaults as fallback).
  const facebookImages = config.branding?.facebookImage
    ? [{ url: config.branding.facebookImage, width: 1200, height: 630 }]
    : null;
  const twitterImages = config.branding?.twitterImage
    ? [{ url: config.branding.twitterImage, width: 600, height: 314 }]
    : null;

  // Structured data: an AboutPage node that references the marketplace
  // Organization entity <Page> already injects (@id "<root>#organization")
  // rather than duplicating that Organization here.
  const aboutPageUrl = `${config.marketplaceRootURL}/p/about`;
  const organizationId = `${config.marketplaceRootURL}#organization`;
  const pageSchema = {
    '@context': 'http://schema.org',
    '@type': 'AboutPage',
    '@id': `${aboutPageUrl}#webpage`,
    url: aboutPageUrl,
    name: schemaTitle,
    description: schemaDescription,
    inLanguage: 'en',
    about: { '@id': organizationId },
    publisher: { '@id': organizationId },
  };

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
      scrollingDisabled={scrollingDisabled}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={pageSchema}
    >
      <LayoutSingleColumn
        mainColumnClassName={css.layoutMain}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        {/* Hero — visually distinct, set full-width and green in Step 3. */}
        <section className={css.hero}>
          <Heading as="h1" rootClassName={css.heroTitle}>
            {intl.formatMessage({ id: 'AboutPage.heroTitle' })}
          </Heading>
          <p className={css.heroSubline}>
            {intl.formatMessage({ id: 'AboutPage.heroSubline' })}
          </p>
        </section>

        <div className={css.content}>
          {/* Why we exist */}
          <section className={css.section}>
            <Heading as="h2" rootClassName={css.sectionTitle}>
              {intl.formatMessage({ id: 'AboutPage.whyExistTitle' })}
            </Heading>
            <p className={css.paragraph}>
              {intl.formatMessage({ id: 'AboutPage.whyExistP1' })}
            </p>
            <p className={css.paragraph}>
              {intl.formatMessage({ id: 'AboutPage.whyExistP2' })}
            </p>
            <p className={css.paragraph}>
              {intl.formatMessage({ id: 'AboutPage.whyExistP3' })}
            </p>
          </section>

          {/* Why we built Pallet Rack Market */}
          <section className={css.section}>
            <Heading as="h2" rootClassName={css.sectionTitle}>
              {intl.formatMessage({ id: 'AboutPage.whyBuiltTitle' }, { marketplaceName })}
            </Heading>
            <p className={css.paragraph}>
              {intl.formatMessage({ id: 'AboutPage.whyBuiltIntro' }, { marketplaceName })}
            </p>
            <p className={css.paragraph}>
              {intl.formatMessage({ id: 'AboutPage.whyBuiltIntro2' }, { marketplaceName })}
            </p>

            <div className={css.feature}>
              <Heading as="h3" rootClassName={css.featureTitle}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltTaxonomyTitle' })}
              </Heading>
              <p className={css.paragraph}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltTaxonomyBody' })}
              </p>
            </div>

            <div className={css.feature}>
              <Heading as="h3" rootClassName={css.featureTitle}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltLeadQuoteTitle' })}
              </Heading>
              <p className={css.paragraph}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltLeadQuoteBody' })}
              </p>
            </div>

            <div className={css.feature}>
              <Heading as="h3" rootClassName={css.featureTitle}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltMultiYardTitle' })}
              </Heading>
              <p className={css.paragraph}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltMultiYardBody' })}
              </p>
            </div>

            <div className={css.feature}>
              <Heading as="h3" rootClassName={css.featureTitle}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltLocalTitle' })}
              </Heading>
              <p className={css.paragraph}>
                {intl.formatMessage({ id: 'AboutPage.whyBuiltLocalBody' })}
              </p>
            </div>
          </section>

          {/* Who it's for */}
          <section className={css.section}>
            <Heading as="h2" rootClassName={css.sectionTitle}>
              {intl.formatMessage({ id: 'AboutPage.whoForTitle' })}
            </Heading>
            <p className={css.paragraph}>
              {intl.formatMessage({ id: 'AboutPage.whoForIntro' }, { marketplaceName })}
            </p>
            <ul className={css.audienceList}>
              <li>{intl.formatMessage({ id: 'AboutPage.whoForItem1' })}</li>
              <li>{intl.formatMessage({ id: 'AboutPage.whoForItem2' })}</li>
              <li>{intl.formatMessage({ id: 'AboutPage.whoForItem3' })}</li>
              <li>{intl.formatMessage({ id: 'AboutPage.whoForItem4' })}</li>
            </ul>
          </section>

          {/* Where we are today */}
          <section className={css.section}>
            <Heading as="h2" rootClassName={css.sectionTitle}>
              {intl.formatMessage({ id: 'AboutPage.todayTitle' })}
            </Heading>
            <p className={css.paragraph}>
              {intl.formatMessage({ id: 'AboutPage.todayBody' })}
            </p>
            <div className={css.ctaRow}>
              <NamedLink name="NewListingPage" className={css.ctaPrimary}>
                {intl.formatMessage({ id: 'AboutPage.ctaListRack' })}
              </NamedLink>
              <NamedLink name="SearchPage" className={css.ctaPrimary}>
                {intl.formatMessage({ id: 'AboutPage.ctaBrowse' })}
              </NamedLink>
            </div>
            <div className={css.storeRow}>
              <ExternalLink href={APP_STORE_URL} className={css.storeButton}>
                <AppleIcon />
                <span className={css.storeButtonText}>
                  <span className={css.storeButtonEyebrow}>
                    {intl.formatMessage({ id: 'AboutPage.storeDownloadOn' })}
                  </span>
                  <span className={css.storeButtonName}>App Store</span>
                </span>
              </ExternalLink>
              {PLAY_STORE_URL ? (
                <ExternalLink href={PLAY_STORE_URL} className={css.storeButton}>
                  <GooglePlayIcon />
                  <span className={css.storeButtonText}>
                    <span className={css.storeButtonEyebrow}>
                      {intl.formatMessage({ id: 'AboutPage.storeGetItOn' })}
                    </span>
                    <span className={css.storeButtonName}>Google Play</span>
                  </span>
                </ExternalLink>
              ) : null}
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const AboutPage = compose(connect(mapStateToProps))(AboutPageComponent);

export default AboutPage;
